/* ==========================================================================
   Crumpaw · cart drawer and cart page

   One controller for both surfaces. Plain browser JavaScript - no imports, no
   build step, no dependency on the theme's module graph, so it keeps working if
   Horizon's own cart modules change underneath it.

   The central decision: after any cart change this asks Shopify to re-render the
   section and swaps the result in, rather than patching the DOM from the cart
   JSON. Money formatting, line item properties, discounts and what counts as a
   gift line are all decided by Liquid in one place. Rebuilding any of that here
   would be a second implementation that could disagree with the first, and the
   number the customer reads has to be the number Shopify charges.

   The drawer is a native <dialog> opened with showModal(): the focus trap and
   Esc come from the browser rather than from a hand-written trap that would have
   to stay correct as the contents are replaced.
   ========================================================================== */

(function () {
  'use strict';

  var CART_UPDATE = 'cart:update';
  var SOURCE = 'cp-cart';

  /* ---------------------------------------------------------------- helpers */

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-cp-cart-root]'));
  }

  function sectionIds() {
    return roots()
      .map(function (r) { return r.getAttribute('data-cp-section-id'); })
      .filter(Boolean)
      .join(',');
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var err = new Error(data && data.description ? data.description : 'Cart request failed');
          err.payload = data;
          err.status = response.status;
          throw err;
        }
        return data;
      });
    });
  }

  /**
   * Swap in freshly rendered section HTML.
   *
   * Only the inner wrapper is replaced, never the <dialog> itself - replacing
   * the dialog would close an open drawer and throw away the browser's focus
   * trap along with it.
   */
  function swap(root, incoming) {
    var current = root.querySelector('[data-cp-cart-inner]');
    if (!incoming || !current) return false;

    var hadFocus = current.contains(document.activeElement);
    current.replaceWith(incoming);

    // The element that had focus has just been thrown away, so put focus
    // somewhere deliberate rather than letting it fall back to <body> and
    // silently drop the user out of the dialog.
    if (hadFocus) {
      var target =
        root.querySelector('[data-cp-close]') ||
        incoming.querySelector('button, [href], input, textarea');
      if (target) target.focus();
    }
    return true;
  }

  /**
   * Apply whatever the Section Rendering API gave us, and report which roots it
   * could not serve.
   *
   * The cart page is a section of its template and renders through the API
   * normally. The drawer is rendered by {% section %} in the layout, and the API
   * does not accept those: asking for it returns the page's own sections and
   * nothing else. So the drawer falls back to re-reading the current page, which
   * is the only way to get its markup rendered by Liquid rather than rebuilt
   * here from cart JSON.
   */
  function applySections(sections) {
    var missed = [];
    roots().forEach(function (root) {
      var id = root.getAttribute('data-cp-section-id');
      var html = sections && sections[id];
      if (!html) {
        missed.push(root);
        return;
      }
      var incoming = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-cp-cart-inner]');
      if (!swap(root, incoming)) missed.push(root);
    });
    return missed;
  }

  function refreshFromPage(targets) {
    if (!targets || !targets.length) return Promise.resolve();

    return fetch(window.location.href, { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        targets.forEach(function (root) {
          var id = root.getAttribute('data-cp-section-id');
          var src = doc.querySelector(
            '[data-cp-cart-root][data-cp-section-id="' + id + '"] [data-cp-cart-inner]'
          );
          if (src) swap(root, document.importNode(src, true));
        });
      })
      .catch(function () {
        /* Leaving the cart as it was beats blanking it. */
      });
  }

  function announce(cart) {
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SOURCE,
          data: { source: SOURCE, itemCount: cart ? cart.item_count : 0 },
        },
      })
    );
  }

  function showError(root, message) {
    var box = root.querySelector('[data-cp-error]');
    if (!box) return;
    box.textContent = message || box.getAttribute('data-cp-generic') || '';
    box.hidden = false;
  }

  function clearError(root) {
    var box = root.querySelector('[data-cp-error]');
    if (box) box.hidden = true;
  }

  function busy(root, on) {
    root.setAttribute('data-cp-loading', on ? 'true' : 'false');
  }

  /* ------------------------------------------------------------- cart calls */

  function request(root, url, body) {
    clearError(root);
    busy(root, true);
    body.sections = sectionIds();
    body.sections_url = window.location.pathname;

    return post(url, body)
      .then(function (data) {
        var pending = refreshFromPage(applySections(data.sections));

        // /cart/change.js and /cart/update.js already return the whole cart, so
        // only /cart/add.js - which returns the line that was added - needs a
        // second call. Shopify throttles bursts of cart requests, so not making
        // one we do not need is the difference between working and a 429.
        if (typeof data.item_count === 'number') {
          return pending.then(function () {
            announce(data);
            reconcileGift(root, data);
            return data;
          });
        }

        return pending
          .then(function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }); })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            announce(cart);
            reconcileGift(root, cart);
            return cart;
          });
      })
      .catch(function (err) {
        var box = root.querySelector('[data-cp-error]');
        var stock = box && box.getAttribute('data-cp-stock');
        var generic = box && box.getAttribute('data-cp-generic');
        showError(root, err && err.status === 422 && stock ? stock : generic);
      })
      .finally(function () {
        busy(root, false);
      });
  }

  function changeLine(root, key, quantity) {
    return request(root, '/cart/change.js', { id: key, quantity: quantity });
  }

  function addVariant(root, id, quantity, properties) {
    var item = { id: id, quantity: quantity || 1 };
    if (properties) item.properties = properties;
    return request(root, '/cart/add.js', { items: [item] });
  }

  /* --------------------------------------------------------------- the gift */

  /**
   * A gift that helped pay for itself is not a gift.
   *
   * If the cart has fallen back below the threshold the gift line is taken out
   * again, and a gift is never allowed above quantity 1. Both are checked
   * against the cart Shopify just returned rather than against anything held
   * here, so a change made in another tab or on the page cannot desync it.
   */
  var reconciling = false;

  function reconcileGift(root, cart) {
    if (reconciling || !cart || !cart.items) return;

    var picker = root.querySelector('[data-cp-gift]');
    if (!picker) return;

    var threshold = parseInt(picker.getAttribute('data-cp-threshold'), 10) || 0;
    var giftLine = null;
    var subtotal = 0;

    cart.items.forEach(function (line) {
      var props = line.properties || {};
      if (props._free_gift) {
        giftLine = line;
      } else {
        subtotal += line.final_line_price;
      }
    });

    if (!giftLine) return;

    if (subtotal < threshold) {
      reconciling = true;
      changeLine(root, giftLine.key, 0).finally(function () { reconciling = false; });
    } else if (giftLine.quantity > 1) {
      reconciling = true;
      changeLine(root, giftLine.key, 1).finally(function () { reconciling = false; });
    }
  }

  function pickGift(root, button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    var variant = button.getAttribute('data-cp-variant');
    var tier = button.getAttribute('data-cp-tier');
    var existing = root.querySelector('[data-cp-gift-line="true"]');

    var first = existing
      ? changeLine(root, existing.getAttribute('data-cp-key'), 0)
      : Promise.resolve();

    first.then(function () {
      // The tier is recorded on the line so the value the gift was earned at
      // travels with the order rather than living only in the theme settings.
      addVariant(root, variant, 1, { _free_gift: 'true', _gift_tier: tier });
    });
  }

  /* ----------------------------------------------------------- the drawer */

  function dialogOf(root) {
    return root.querySelector('[data-cp-dialog]');
  }

  function openDrawer(root) {
    var dialog = dialogOf(root);
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  function closeDrawer(root) {
    var dialog = dialogOf(root);
    if (!dialog || !dialog.open) return;

    dialog.setAttribute('data-cp-closing', '');
    var done = function () {
      dialog.removeAttribute('data-cp-closing');
      dialog.close();
      document.documentElement.style.overflow = '';
    };

    var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches) {
      done();
    } else {
      var fallback = setTimeout(done, 320);
      dialog.addEventListener(
        'animationend',
        function () { clearTimeout(fallback); done(); },
        { once: true }
      );
    }
  }

  /** Opened from anywhere - the header icon, a sticky bar, a theme-editor click. */
  window.cpCartDrawer = {
    open: function () {
      var root = document.querySelector('cp-cart-drawer[data-cp-cart-root]');
      if (root) openDrawer(root);
      return !!root;
    },
    close: function () {
      var root = document.querySelector('cp-cart-drawer[data-cp-cart-root]');
      if (root) closeDrawer(root);
    },
  };

  /* --------------------------------------------------------------- wiring */

  function onClick(event) {
    var root = event.target.closest('[data-cp-cart-root]');
    if (!root) {
      // The header trigger lives outside the section.
      var opener = event.target.closest('[data-cp-cart-open]');
      if (opener && window.cpCartDrawer.open()) event.preventDefault();
      return;
    }

    var el = event.target;

    var close = el.closest('[data-cp-close]');
    if (close) {
      closeDrawer(root);
      return;
    }

    var qty = el.closest('[data-cp-qty]');
    if (qty) {
      var itemEl = qty.closest('[data-cp-item]');
      var value = itemEl.querySelector('[data-cp-qty-value]');
      var current = parseInt(value.textContent.trim(), 10) || 0;
      var next = qty.getAttribute('data-cp-qty') === 'up' ? current + 1 : current - 1;
      if (next < 0) next = 0;
      itemEl.setAttribute('data-cp-busy', 'true');
      changeLine(root, itemEl.getAttribute('data-cp-key'), next);
      return;
    }

    var remove = el.closest('[data-cp-remove]');
    if (remove) {
      var line = remove.closest('[data-cp-item]');
      line.setAttribute('data-cp-busy', 'true');
      changeLine(root, line.getAttribute('data-cp-key'), 0);
      return;
    }

    var upsell = el.closest('[data-cp-upsell-add]');
    if (upsell) {
      if (upsell.getAttribute('data-cp-added') === 'true') return;
      upsell.disabled = true;
      addVariant(root, upsell.getAttribute('data-cp-variant'), 1);
      return;
    }

    var gift = el.closest('[data-cp-gift-pick]');
    if (gift) {
      pickGift(root, gift);
      return;
    }

    var apply = el.closest('[data-cp-discount-apply]');
    if (apply) {
      var input = root.querySelector('[data-cp-discount-input]');
      var code = input && input.value.trim();
      if (!code) return;
      // Shopify has no AJAX endpoint for applying a code to the cart. The
      // /discount/ route is the supported way, and it comes straight back.
      window.location.href =
        '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }
  }

  /**
   * The bowl add-on's colour.
   *
   * Each colour radio carries the strings Liquid formatted for that variant -
   * price, compare-at, saving, button label, photo - and this only copies them
   * onto the card. The Add button is the ordinary upsell button, so pointing
   * its data-cp-variant at the chosen colour is all it takes for the add to
   * post that colour. Nothing is posted until the button is pressed.
   */
  function pickAddonColour(input) {
    var card = input.closest('[data-cp-mat]');
    if (!card) return;
    var d = input.dataset;

    var button = card.querySelector('[data-cp-upsell-add]');
    if (button && button.getAttribute('data-cp-added') !== 'true') {
      button.setAttribute('data-cp-variant', input.value);
      if (d.cpAdd) button.textContent = d.cpAdd;
    }

    var price = card.querySelector('[data-cp-mat-price]');
    if (price && d.cpPrice) price.textContent = d.cpPrice;

    var was = card.querySelector('[data-cp-mat-was]');
    if (was) {
      was.textContent = d.cpCompare || '';
      was.hidden = !d.cpCompare;
    }

    // The saving pill, the photo badge and the line under the button all exist
    // only when this colour has a compare-at price; an empty string hides each.
    [
      ['[data-cp-mat-save]', d.cpSave],
      ['[data-cp-mat-pct]', d.cpPct],
      ['[data-cp-mat-note]', d.cpNote],
    ].forEach(function (pair) {
      var node = card.querySelector(pair[0]);
      if (!node) return;
      node.textContent = pair[1] || '';
      node.hidden = !pair[1];
    });

    var img = card.querySelector('[data-cp-mat-img]');
    if (img && d.cpImg) {
      img.src = d.cpImg;
      img.alt = d.cpAlt || '';
    }
  }

  /** The note is saved when the customer leaves the field, not on every keypress. */
  function onChange(event) {
    var colour = event.target.closest('[data-cp-mat-colour]');
    if (colour) {
      pickAddonColour(colour);
      return;
    }

    var note = event.target.closest('[data-cp-note-input]');
    if (!note) return;
    var root = note.closest('[data-cp-cart-root]');
    if (!root) return;

    var status = root.querySelector('[data-cp-note-status]');
    var original = status ? status.textContent : '';

    post('/cart/update.js', { note: note.value })
      .then(function () {
        if (status) status.textContent = original;
      })
      .catch(function () {
        showError(root);
      });
  }

  /**
   * Re-render every cart surface from the Section Rendering API.
   *
   * Note what is *not* on this request: an `Accept: application/json` header.
   * On a product URL that header makes Shopify answer with the product's own
   * JSON and drop the `sections` parameter on the floor, so the sections came
   * back missing and every add-to-cart fell through to refreshFromPage - a
   * second fetch of the whole product page, half a megabyte of HTML, parsed
   * with DOMParser, all before the drawer was allowed to open. The Sections
   * API answers in JSON regardless of Accept, so asking for it is the one thing
   * that stops it arriving.
   */
  function renderSections() {
    var ids = sectionIds();
    if (!ids) return Promise.resolve();

    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids))
      .then(function (r) { return r.json(); })
      .catch(function () { return {}; })
      .then(function (sections) { return refreshFromPage(applySections(sections)); });
  }

  /**
   * Renders run one at a time, and the panel stays busy until the last one in
   * has landed.
   *
   * One click can add more than one line: the harness posts its lead and
   * second-dog lines once the form's own add has come back, and each one
   * broadcasts separately. Overlapping renders would race to replace the same
   * wrapper, and clearing the busy flag per render would flicker the panel
   * ready and straight back again in between.
   */
  var renderQueue = Promise.resolve();
  var renderDepth = 0;

  function queueRender() {
    renderDepth += 1;
    roots().forEach(function (root) { busy(root, true); });

    var settle = function () {
      renderDepth -= 1;
      if (renderDepth <= 0) {
        renderDepth = 0;
        roots().forEach(function (root) { busy(root, false); });
      }
    };

    // Both arms of every then(), so a failed render still hands the queue on
    // and still gives the panel back. A cart stuck behind a permanent spinner
    // is worse than one showing a count that is a moment out of date.
    renderQueue = renderQueue
      .then(renderSections, renderSections)
      .then(settle, settle);
  }

  /**
   * Open the drawer the instant an add-to-cart form is submitted, before any of
   * it has reached Shopify.
   *
   * Opening on `cart:update` still meant waiting out /cart/add.js and then the
   * /cart.js read behind it - the better part of a second measured on this
   * store, all of it with nothing at all happening on screen, which is long
   * enough that shoppers press the button a second time. The panel opens now,
   * marked busy, and the line appears in it when the server says what it is.
   *
   * `optimistic` remembers that this opened the drawer rather than the shopper,
   * so a failed add can put it back - and only while nothing has landed since,
   * because an error on a later add must not close a drawer being read.
   */
  var optimistic = null;
  var optimisticTimer = null;

  function giveUp() {
    if (!optimistic) return;
    var drawer = optimistic;
    optimistic = null;
    clearTimeout(optimisticTimer);
    roots().forEach(function (root) { busy(root, false); });
    closeDrawer(drawer);
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

    var drawer = document.querySelector('cp-cart-drawer[data-cp-open-on-add]');
    var dialog = drawer && dialogOf(drawer);
    if (!dialog || dialog.open) return;

    optimistic = drawer;
    openDrawer(drawer);
    roots().forEach(function (root) { busy(root, true); });

    // If neither an update nor an error ever arrives - a dropped connection, a
    // handler that bailed before posting - the panel must not sit spinning over
    // a cart that never changed.
    clearTimeout(optimisticTimer);
    optimisticTimer = setTimeout(giveUp, 8000);
  }

  function onCartUpdate(event) {
    var detail = event.detail || {};
    var data = detail.data || {};
    if (data.source === SOURCE) return; // our own change, already rendered

    if (data.didError) {
      giveUp();
      return;
    }

    // The add landed, so the drawer is the shopper's now rather than on
    // approval, and opening is a no-op if onSubmit already did it.
    optimistic = null;
    clearTimeout(optimisticTimer);

    var drawer = document.querySelector('cp-cart-drawer[data-cp-open-on-add]');
    if (drawer) openDrawer(drawer);

    queueRender();
  }

  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange);
  document.addEventListener('submit', onSubmit);
  document.addEventListener(CART_UPDATE, onCartUpdate);
  document.addEventListener('cart:error', giveUp);

  // The browser fires this for Esc and for the backdrop, so scroll lock is
  // released however the dialog was dismissed.
  document.addEventListener(
    'close',
    function (event) {
      if (event.target && event.target.matches('[data-cp-dialog]')) {
        document.documentElement.style.overflow = '';
      }
    },
    true
  );

  // Clicking the backdrop of a modal dialog targets the dialog itself.
  document.addEventListener('click', function (event) {
    var dialog = event.target;
    if (!dialog.matches || !dialog.matches('[data-cp-dialog]')) return;
    var box = dialog.getBoundingClientRect();
    var inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom;
    if (!inside) {
      var root = dialog.closest('[data-cp-cart-root]');
      if (root) closeDrawer(root);
    }
  });
})();
